// ── Report-Model-Engine ────────────────────────────────────────────────────
// EINE kanonische Quelle für die Zahlen-Ableitung der Exporte (PDF + Excel).
// Ziel: jede Zahl wird EINMAL hier gerechnet, über benannte Felder geliefert, und
// vor dem Versand laut auf Konsistenz geprüft. Mit dem Export geht der Kunde zum
// Finanzamt — eine falsche oder falsch platzierte Zahl ist nicht akzeptabel.
//
// WICHTIG: Diese Datei rechnet KEINE Steuer. Die Steuer-Wahrheit kommt aus
// fifo.js / austrianCost.js / reportEngine.js (326 Tests). Hier geht es NUR um die
// getreue, identische Darstellung dieser Zahlen in beiden Ausleitungen.
//
// Pure ES-Module-Funktionen — keine Browser-/Firebase-/Node-Deps (wird sowohl vom
// Client als auch serverseitig in api/_lib importiert, genau wie exportData.js).
//
// Hinweis Modul-Zyklus: wir importieren buildDisposalLotRows aus exportData.js,
// und exportData.js importiert SWAP_EUR_SET/deriveSwapEurValue von hier. Beide
// Nutzungen passieren NUR zur Aufrufzeit (in Funktionskörpern), nie beim
// Modul-Init → der Zyklus ist unkritisch (live bindings / call-time property access).
import { buildDisposalLotRows } from './exportData';

// ── Swap-EUR-Ableitung: EINE Whitelist, EINE Funktion ──────────────────────
// Vorher lagen drei divergierende Sets vor (exportData STABLE_FIAT_FOR_VALUE = 23,
// pdfBuilder STABLECOINS_PEGGED = 16, excelBuilder SWAP_EUR_PEGGED = 15). Dadurch
// konnte ein fiat-/stable-quotierter Swap in PDF, Excel und der Transfer-
// Anreicherung VERSCHIEDEN bewertet werden. Hier ist die einzige Quelle.
//
// Bekannte Näherung: Fiat-Beträge (USD/GBP/CHF/…) werden ohne Wechselkurs als ≈EUR
// behandelt — wie es die App an allen anderen Stellen bereits tut. EUR und
// EUR-Stablecoins sind exakt; USD/sonstige Fiat sind eine bewusste Vereinfachung.
export const SWAP_EUR_SET = new Set([
  // USD-/EUR-Stablecoins (≈ 1:1 zum jeweiligen Fiat)
  'USDC', 'USDT', 'DAI', 'BUSD', 'TUSD', 'FDUSD', 'USDE', 'PYUSD', 'GUSD', 'USDP',
  'EURC', 'EURT', 'EURI', 'EURS',
  // Echte Fiat-Quotes
  'EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL',
]);

function upper(s) { return String(s || '').toUpperCase(); }

// Coin-Symbole eines (ggf. unnormalisierten) Swaps. Roh-TXs aus Import/AI nutzen
// symOut/symIn; canonicalNormalizer setzt coinOut/coinIn nur ADDITIV obendrauf —
// daher müssen wir wie mergeTxsByDay BEIDE lesen.
export function swapOutSym(tx) { return upper(tx.coinOut || tx.symOut); }
export function swapInSym(tx)  { return upper(tx.coinIn  || tx.symIn); }

// EUR-Wert eines Swaps aus der Stablecoin-/Fiat-Seite (0 = nicht ableitbar).
// Bevorzugt KEINE Roh-preis-Logik — Aufrufer entscheiden, ob ein erfasster preis
// Vorrang hat (tx.preis > 0 ? tx.preis : deriveSwapEurValue(tx)).
export function deriveSwapEurValue(tx) {
  if (SWAP_EUR_SET.has(swapOutSym(tx))) return Number(tx.mengeOut) || 0;
  if (SWAP_EUR_SET.has(swapInSym(tx)))  return Number(tx.mengeIn)  || 0;
  return 0;
}

// ── Kanonisches TX-Zeilenmodell (eine Ableitung für PDF + Excel) ───────────
// Stückpreis, Gesamt (EUR) und Swap-EUR werden GENAU EINMAL hier abgeleitet —
// vorher rechneten TxTable (PDF) und txToRow (Excel) je separat (Divergenz-Risiko,
// realer XLM-Fehler). Liefert Roh-Zahlen; die Renderer formatieren medien-
// spezifisch (PDF dynamische Dezimalen, Excel volle Präzision + numFmt) — der
// WERT ist in beiden identisch.
export function buildTxRow(tx) {
  const isSwap   = tx.typ === 'swap';
  const preis    = Number(tx.preis) || 0;
  const eff      = preis > 0 ? preis : (isSwap ? deriveSwapEurValue(tx) : 0);
  const menge    = Number(tx.menge) || 0;
  const mengeOut = Number(tx.mengeOut) || 0;
  const mengeIn  = Number(tx.mengeIn) || 0;
  let stueck = null; // €/Einheit
  if (isSwap) { if (mengeOut > 0 && eff > 0) stueck = eff / mengeOut; }
  else if (menge > 0 && eff > 0) stueck = eff / menge;
  const coinOut = swapOutSym(tx);
  const coinIn  = swapInSym(tx);
  const coin    = upper(tx.coin || tx.sym);
  return {
    datum:    tx.datum || null,
    typ:      tx.typ || '',
    isSwap,
    coin:     coin || null,
    coinOut,
    coinIn,
    coinSym:  isSwap ? coinOut : coin,          // für Icon-Lookup
    coinLabel: isSwap ? `${coinOut} → ${coinIn}` : (coin || '–'),
    menge,
    mengeOut,
    mengeIn,
    stueck,                                      // null = nicht ableitbar
    gesamt:   eff,                               // EUR-Gesamtwert (0 = keiner)
    gebuehr:  Number(tx.gebuehr) || 0,
    exchange: tx.exchange || '',
    notiz:    tx.notiz || '',
    hash:     tx.txHash || tx.hash || '',
    estimated: !!tx._valueEstimated,             // Wert aus Oracle abgeleitet (≈)
    _docId:   tx._docId,
    id:       tx.id,
  };
}

// ── Veräußerungs-Aufschlüsselung je Event (eine Quelle für PDF + Excel) ────
// Wrapper über buildDisposalLotRows: gruppiert die flachen Lot-Zeilen pro
// Realisierungs-Event. PDF (LotProofBlock) UND Excel (Disposal-Sheets) rendern
// danach aus DERSELBEN Berechnung → byte-identische Lot-Zahlen.
export function groupDisposalRowsByEvent(taxableEvents, opts = {}) {
  const events = Array.isArray(taxableEvents) ? taxableEvents : [];
  return events.map((ev) => ({ ev, rows: buildDisposalLotRows([ev], opts) }));
}

// ── Reconciliation-Gate ─────────────────────────────────────────────────────
// Mit dem Export geht der Kunde zum Finanzamt. Dieses Gate scheitert LAUT, bevor
// eine Datei mit nicht aufgehenden Summen oder NaN den Server verlässt — der
// API-Handler verwandelt den Wurf in HTTP 500 statt einer falschen Datei.
export class ReportIntegrityError extends Error {
  constructor(message, problems) {
    super(message);
    this.name = 'ReportIntegrityError';
    this.problems = problems || [];
  }
}

function isFiniteNum(n) {
  return Number.isFinite(typeof n === 'number' ? n : Number(n));
}
// Toleranz: die Engine rundet auf 2 Dezimalen pro Lot; über mehrere Lots
// akkumuliert sich minimal Rundung. 2 Cent absolut ODER 0,1 % relativ.
function approxEq(a, b) {
  const diff = Math.abs(Number(a) - Number(b));
  return diff <= Math.max(0.02, Math.abs(Number(b) || 0) * 0.001);
}

export function assertReportIntegrity(data, opts = {}) {
  const problems = [];
  const warn = opts.onWarn || ((m) => {
    if (typeof console !== 'undefined') console.warn('[reportIntegrity]', m);
  });
  const events = Array.isArray(data?.taxableEvents) ? data.taxableEvents : [];
  const costBasisMethod = data?.costBasisMethod;

  // 1) HART: pro Veräußerungs-Event muss die angezeigte Lot-Aufschlüsselung zur
  //    Engine-Kennzahl passen (Σ Lots == Event). Wenn das bricht, widersprechen
  //    sich die Zahlen auf dem Blatt selbst — unzulässig.
  for (const ev of events) {
    const coin = ev.coin || ev.coinOut || '?';
    const tag  = `${coin} ${ev.datum || '?'}`;
    let lots;
    try {
      lots = buildDisposalLotRows([ev], { costBasisMethod });
    } catch (e) {
      problems.push(`${tag}: Lot-Aufschlüsselung warf ${e?.message || e}`);
      continue;
    }
    const evMenge = Number(ev.menge || ev.mengeOut || 0);
    const evAk    = Number(ev.ak ?? ev.anschaffung ?? 0);
    const evGv    = ev.gv == null ? null : Number(ev.gv);
    const unknown = !!ev.costBasisUnknown || (lots.length > 0 && lots.every((l) => l.unbekannt));

    const sMenge = lots.reduce((s, l) => s + (Number(l.lotMenge) || 0), 0);
    if (!approxEq(sMenge, evMenge)) problems.push(`${tag}: Σ Lot-Menge ${sMenge} ≠ Event ${evMenge}`);

    if (!unknown) {
      const sAk = lots.reduce((s, l) => s + (Number(l.anschaffungsKosten) || 0), 0);
      const sGv = lots.reduce((s, l) => s + (Number(l.gv) || 0), 0);
      if (!approxEq(sAk, evAk)) problems.push(`${tag}: Σ Anschaffung ${sAk} ≠ Event ${evAk}`);
      if (evGv != null && !approxEq(sGv, evGv)) problems.push(`${tag}: Σ G/V ${sGv} ≠ Event ${evGv}`);
      // Per-Lot-Identität: Verkaufspreis − Gebühr − Anschaffung == G/V
      for (const l of lots) {
        if (l.gv == null) continue;
        const lhs = (Number(l.verkaufspreis) || 0) - (Number(l.gebuehr) || 0) - (Number(l.anschaffungsKosten) || 0);
        if (!approxEq(lhs, l.gv)) problems.push(`${tag}: Lot-Identität ${lhs} ≠ G/V ${l.gv}`);
      }
    }
    // NaN/Infinity auf den Event-Zahlen
    for (const [k, v] of [['gv', evGv], ['ak', evAk], ['menge', evMenge]]) {
      if (v != null && !isFiniteNum(v)) problems.push(`${tag}: ${k} ist nicht endlich (${v})`);
    }
  }

  // 2) HART: kein NaN/Infinity in den gerenderten TX-Zahlen.
  const txLists = [];
  if (Array.isArray(data?.allTxs)) txLists.push(...data.allTxs);
  if (Array.isArray(data?.transactions)) txLists.push(...data.transactions);
  for (const b of (data?.perAsset || [])) {
    if (Array.isArray(b?.transactions)) txLists.push(...b.transactions);
  }
  for (const tx of txLists) {
    for (const k of ['preis', 'menge', 'mengeOut', 'mengeIn', 'gebuehr']) {
      const v = tx?.[k];
      if (v !== undefined && v !== null && !isFiniteNum(v)) {
        problems.push(`TX ${tx.typ || '?'} ${tx.coin || tx.coinOut || ''}: ${k} nicht endlich (${v})`);
      }
    }
  }

  // 3) WEICH (nur Warnung): Cross-Engine-Jahressumme. Σ(Event-G/V) sollte der
  //    realisierten Jahreszahl entsprechen; kleine, methodisch bedingte
  //    Abweichungen blockieren den Export NICHT (sonst falsch-positive 500er).
  if (data?.yearTotals && events.length) {
    const sGv = events.reduce((s, ev) => s + (ev.gv == null ? 0 : (Number(ev.gv) || 0)), 0);
    const realized = Number(data.yearTotals.realized) || 0;
    if (Math.abs(sGv - realized) > Math.max(0.5, Math.abs(realized) * 0.01)) {
      warn(`Jahres-Realisiert weicht ab: Σ Events ${sGv.toFixed(2)} vs. Report ${realized.toFixed(2)}`);
    }
  }

  if (problems.length) {
    throw new ReportIntegrityError(
      `Report-Integritätsprüfung fehlgeschlagen (${problems.length} Problem(e)) — Export blockiert.`,
      problems,
    );
  }
  return true;
}
